import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import TextField from '@mulesoft/anypoint-components/lib/TextField';
import Select from '@mulesoft/anypoint-components/lib/Select';
import styles from '../AuthenticationFields.css';
import { VERTEX_AI_LOCATION_OPTIONS } from '../selectOptions';

/**
 * Google Vertex AI Service Account Authentication Form
 *
 * Renders authentication fields for Google Vertex AI using Service Account:
 * - Service Account Email
 * - Project ID
 * - Private Key (sensitive - cleared in edit mode)
 * - Location
 */
const ServiceAccountForm = ({ mode, formData, onChange }) => {
  const locationOptions = useMemo(() => VERTEX_AI_LOCATION_OPTIONS, []);

  const handleServiceAccountEmailChange = useCallback(
    (e) => onChange('serviceAccountEmail', e.value),
    [onChange]
  );

  const handleProjectIdChange = useCallback(
    (e) => onChange('projectId', e.value),
    [onChange]
  );

  const handlePrivateKeyChange = useCallback(
    (e) => onChange('privateKey', e.value),
    [onChange]
  );

  const handleLocationChange = useCallback(
    (e) => onChange('location', e.value),
    [onChange]
  );

  return (
    <div
      className={styles['auth-fields']}
      data-test-id="vertex-ai-service-account-fields"
    >
      <div className={styles.field}>
        <label htmlFor="service-account-email" className={styles.label}>
          Account email ID <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="service-account-email"
            name="serviceAccountEmail"
            value={formData.serviceAccountEmail || ''}
            onChange={handleServiceAccountEmailChange}
            required
            placeholder="Enter service account email"
            hint="The email address of your Google Cloud service account. Found in IAM & Admin > Service Accounts in Google Cloud Console."
            testId="service-account-email-input"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="project-id" className={styles.label}>
          Project ID <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="project-id"
            name="projectId"
            value={formData.projectId || ''}
            onChange={handleProjectIdChange}
            required
            placeholder="Enter project id"
            hint="Your Google Cloud project identifier. Found in the Google Cloud Console project selector or in your project settings."
            testId="project-id-input"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="private-key" className={styles.label}>
          Secret access key{' '}
          {mode === 'add' && <span className={styles.required}>*</span>}
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="private-key"
            name="privateKey"
            type="password"
            value={formData.privateKey || ''}
            onChange={handlePrivateKeyChange}
            required={mode === 'add'}
            placeholder={
              mode === 'edit'
                ? 'Enter new key to update (leave empty to keep existing)'
                : 'Enter private key'
            }
            hint="The private key from your service account JSON key file. Found in IAM & Admin > Service Accounts > Keys."
            testId="private-key-input"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="location" className={styles.label}>
          Location <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <Select
            id="location"
            name="location"
            testId="location-select"
            value={formData.location || ''}
            placeholder="Select Vertex AI location"
            options={locationOptions}
            onChange={handleLocationChange}
          />
        </div>
      </div>
    </div>
  );
};

ServiceAccountForm.propTypes = {
  mode: PropTypes.oneOf(['add', 'edit']),
  formData: PropTypes.shape({
    serviceAccountEmail: PropTypes.string,
    projectId: PropTypes.string,
    privateKey: PropTypes.string,
    location: PropTypes.string
  }).isRequired,
  onChange: PropTypes.func.isRequired
};

ServiceAccountForm.defaultProps = {
  mode: 'add'
};

export default ServiceAccountForm;
